import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi, ApiError } from '../api/client';
import type { RoleId } from '../api/client';
import {
  PERMISSIONS_MATRIX,
  PERMISSION_GROUPS,
  ROLE_ORDER,
  type PermissionRow,
} from '../framework/permissions';
import './PermissionsMatrix.css';

/**
 * Editable role-permissions matrix at /admin/permissions.
 *
 * Each (permission, role) cell renders as a binary toggle. Defaults
 * come from src/framework/permissions.ts; overrides persist to
 * dbo.role_permission_override on save. Loading on mount calls the
 * overrides endpoint and merges with the defaults; saving sends only
 * the rows that diverge from defaults.
 *
 * Until enforcement is wired to consult this table (Phase 2 follow-up),
 * the matrix is informational + configuration-staging only. Existing
 * hasMinimumRole gates in the API don't yet read these overrides.
 */

type OverridesMap = Map<string, boolean>; // key = `${permId}|${roleId}`

const keyOf = (permId: string, roleId: RoleId): string => `${permId}|${roleId}`;

type ListResponse = {
  rows: Array<{
    permission_id: string;
    role_id: RoleId;
    allowed: boolean;
  }>;
};

export function PermissionsMatrix() {
  const { t } = useTranslation();
  const callApi = useApi();

  // The "effective" matrix = defaults merged with persisted overrides.
  // `effective` mirrors the saved state; `pending` holds unsaved edits.
  const [effective, setEffective] = useState<OverridesMap>(new Map());
  const [pending, setPending] = useState<OverridesMap>(new Map());
  const [loadState, setLoadState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok' }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });
  const [saveState, setSaveState] = useState<
    | { kind: 'idle' }
    | { kind: 'saving' }
    | { kind: 'saved' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Build the baseline map from defaults.
  const defaultsMap = useMemo<OverridesMap>(() => {
    const map = new Map<string, boolean>();
    for (const row of PERMISSIONS_MATRIX) {
      for (const roleId of ROLE_ORDER) {
        map.set(keyOf(row.id, roleId), row.access[roleId]);
      }
    }
    return map;
  }, []);

  // Load persisted overrides on mount.
  useEffect(() => {
    let cancelled = false;
    setLoadState({ kind: 'loading' });
    void (async () => {
      try {
        const data = await callApi<ListResponse>('/admin/permissions');
        if (cancelled) return;
        const merged = new Map(defaultsMap);
        for (const r of data.rows) {
          merged.set(keyOf(r.permission_id, r.role_id), r.allowed);
        }
        setEffective(merged);
        setLoadState({ kind: 'ok' });
      } catch (err) {
        if (cancelled) return;
        // 404 / not-yet-deployed endpoint: just use defaults.
        if (err instanceof ApiError && err.status === 404) {
          setEffective(new Map(defaultsMap));
          setLoadState({ kind: 'ok' });
          return;
        }
        const message =
          err instanceof ApiError
            ? `${String(err.status)} — ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Failed to load permissions';
        setLoadState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsMap]);

  const cellValue = useCallback(
    (permId: string, roleId: RoleId): boolean => {
      const key = keyOf(permId, roleId);
      if (pending.has(key)) return pending.get(key) ?? false;
      return effective.get(key) ?? false;
    },
    [effective, pending],
  );

  const handleToggle = (permId: string, roleId: RoleId) => {
    const key = keyOf(permId, roleId);
    const current = cellValue(permId, roleId);
    const next = !current;
    setPending((prev) => {
      const m = new Map(prev);
      // If the toggle returns the cell to the effective baseline, drop
      // the pending entry (no-op change).
      if (effective.get(key) === next) {
        m.delete(key);
      } else {
        m.set(key, next);
      }
      return m;
    });
    if (saveState.kind === 'saved') {
      setSaveState({ kind: 'idle' });
    }
  };

  const handleSave = async () => {
    if (pending.size === 0) return;
    setSaveState({ kind: 'saving' });
    try {
      const changes = Array.from(pending.entries()).map(([key, allowed]) => {
        const [permission_id, role_id] = key.split('|');
        return { permission_id, role_id, allowed };
      });
      await callApi('/admin/permissions', {
        method: 'PUT',
        body: JSON.stringify({ changes }),
        headers: { 'content-type': 'application/json' },
      });
      // Merge pending into effective.
      setEffective((prev) => {
        const m = new Map(prev);
        for (const [key, val] of pending) m.set(key, val);
        return m;
      });
      setPending(new Map());
      setSaveState({ kind: 'saved' });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${String(err.status)} — ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Save failed';
      setSaveState({ kind: 'error', message });
    }
  };

  const handleDiscard = () => {
    setPending(new Map());
    setSaveState({ kind: 'idle' });
  };

  return (
    <article className="perm-matrix" aria-labelledby="perm-matrix-title">
      <header className="perm-matrix__header">
        <h1 id="perm-matrix-title" className="perm-matrix__title">
          {t('permissions.title')}
        </h1>
        <p className="perm-matrix__subtitle">{t('permissions.subtitle')}</p>
      </header>

      {loadState.kind === 'loading' && (
        <p className="perm-matrix__status">{t('permissions.loading')}</p>
      )}
      {loadState.kind === 'error' && (
        <p className="perm-matrix__status perm-matrix__status--error">
          {loadState.message}
        </p>
      )}

      {loadState.kind === 'ok' && (
        <>
          <div className="perm-matrix__table-wrap">
            <table className="perm-matrix__table">
              <thead>
                <tr>
                  <th scope="col" className="perm-matrix__col-permission">
                    {t('permissions.col.permission')}
                  </th>
                  {ROLE_ORDER.map((roleId) => (
                    <th
                      key={roleId}
                      scope="col"
                      className="perm-matrix__col-role"
                    >
                      {t(`admin.role.${roleId}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_GROUPS.map((g) => (
                  <PermissionGroupRows
                    key={g.id}
                    group={g.id}
                    rows={PERMISSIONS_MATRIX}
                    cellValue={cellValue}
                    onToggle={handleToggle}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {pending.size > 0 && (
            <div className="perm-matrix__save-bar">
              <span className="perm-matrix__save-status">
                {t('permissions.unsavedCount', { count: pending.size })}
              </span>
              <div className="perm-matrix__save-actions">
                <button
                  type="button"
                  className="auth-button auth-button--secondary"
                  onClick={handleDiscard}
                  disabled={saveState.kind === 'saving'}
                >
                  {t('permissions.discard')}
                </button>
                <button
                  type="button"
                  className="auth-button auth-button--primary"
                  onClick={() => void handleSave()}
                  disabled={saveState.kind === 'saving'}
                >
                  {saveState.kind === 'saving'
                    ? t('permissions.saving')
                    : t('permissions.save')}
                </button>
              </div>
            </div>
          )}

          {saveState.kind === 'saved' && (
            <p className="perm-matrix__status perm-matrix__status--ok">
              {t('permissions.savedToast')}
            </p>
          )}
          {saveState.kind === 'error' && (
            <p className="perm-matrix__status perm-matrix__status--error">
              {saveState.message}
            </p>
          )}
        </>
      )}

      <p className="perm-matrix__footnote">{t('permissions.footnote')}</p>
    </article>
  );
}

function PermissionGroupRows({
  group,
  rows,
  cellValue,
  onToggle,
}: {
  group: PermissionRow['group'];
  rows: PermissionRow[];
  cellValue: (permId: string, roleId: RoleId) => boolean;
  onToggle: (permId: string, roleId: RoleId) => void;
}) {
  const { t } = useTranslation();
  const groupRows = rows.filter((r) => r.group === group);
  if (groupRows.length === 0) return null;
  return (
    <>
      <tr className="perm-matrix__group-row">
        <th
          scope="rowgroup"
          colSpan={ROLE_ORDER.length + 1}
          className="perm-matrix__group-heading"
        >
          {t(`permissions.group.${group}`)}
        </th>
      </tr>
      {groupRows.map((row) => (
        <tr key={row.id} className="perm-matrix__row">
          <th scope="row" className="perm-matrix__row-label">
            <span className="perm-matrix__row-title">{row.label}</span>
            <span className="perm-matrix__row-desc">{row.description}</span>
          </th>
          {ROLE_ORDER.map((roleId) => (
            <td key={roleId} className="perm-matrix__data">
              <PermissionToggle
                allowed={cellValue(row.id, roleId)}
                onChange={() => onToggle(row.id, roleId)}
                ariaLabel={`${row.label} — ${roleId}`}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function PermissionToggle({
  allowed,
  onChange,
  ariaLabel,
}: {
  allowed: boolean;
  onChange: () => void;
  ariaLabel: string;
}) {
  return (
    <label className="perm-toggle" aria-label={ariaLabel}>
      <input
        type="checkbox"
        className="perm-toggle__input"
        checked={allowed}
        onChange={onChange}
      />
      <span className="perm-toggle__slider" />
    </label>
  );
}

export default PermissionsMatrix;
